// KPI değer yazımı / sürüm / düzeltme şemaları — schemas/kpi.ts 600 satır bölmesi (TODO-KPI-114/-144, salt taşıma); `kpi.ts` barrel'i yeniden dışa açar.
import { z } from "zod";
import { PeriodType } from "./period.js";
import { KPI_BREAKDOWN_ROLLUPS, KPI_VALUE_STATUSES, KPI_WORKFLOW_STATES } from "./kpi-constants.js";

// ---- Değer yazımı (kpi_values upsert) ----
export const KpiValueUpsertSchema = z
  .object({
    metricId: z.string().uuid(),
    entityId: z.string().uuid(),
    year: z.number().int().min(1990).max(2100),
    periodType: PeriodType.default("annual"),
    status: z.enum(KPI_VALUE_STATUSES),
    /** Yazım anındaki gösterge birimi (null = birimsiz) — zorunlu anahtar. */
    unit: z.string().max(32).nullable(),
    numericValue: z.number().optional(),
    textValue: z.string().max(2000).optional(),
    note: z.string().max(2000).optional(),
    footnote: z.string().max(2000).optional(),
    /** Portal: hücre not iş parçacığına yazılır (değer yazımıyla aynı tx); `kpi_values`'a yazılmaz. */
    clientNote: z.string().max(2000).optional(),
    clientUpdatedAt: z.string().optional(),
    /** M17 — onaylı değer değiştirilirken neden (raporda "Düzeltmeler" dipnotu); yalnız iç aktör. */
    changeReason: z.string().max(500).optional(),
    /** M17 (0055) — dış sistem kimliği (ERP/API); yalnız iç aktör, portal gövdesinde yok sayılır. */
    source: z
      .object({
        system: z.string().trim().min(1).max(64),
        externalId: z.string().trim().min(1).max(128),
      })
      .optional(),
    /** M17 (0055) — yeniden deneme güvenliği; `Idempotency-Key` başlığı gövdeye üstün gelir. */
    idempotencyKey: z.string().min(1).max(128).optional(),
  })
  .refine(
    (v) =>
      v.status !== "filled" ||
      (v.numericValue !== undefined && Number.isFinite(v.numericValue)) ||
      (v.textValue !== undefined && v.textValue.length > 0),
    { message: "Dolu değer için sayısal ya da metin değer gerekli", path: ["numericValue"] },
  );
export type KpiValueUpsert = z.infer<typeof KpiValueUpsertSchema>;

// ---- M17 — değer sürüm tarihçesi (kpi_value_versions, migration 0054) ----
export const KPI_CHANGE_KINDS = [
  "write",
  "verify",
  "clarify",
  "pin",
  "approve",
  "import",
  "move",
  "reported",
  "evidence",
  "unit_change",
] as const;
export type KpiChangeKind = (typeof KPI_CHANGE_KINDS)[number];

export const KpiValueVersionSchema = z.object({
  id: z.string().uuid(),
  valueId: z.string().uuid(),
  entityId: z.string().uuid(),
  version: z.number().int(),
  status: z.enum(KPI_VALUE_STATUSES),
  numericValue: z.number().nullable(),
  textValue: z.string().nullable(),
  workflowState: z.enum(KPI_WORKFLOW_STATES),
  isReported: z.boolean(),
  pinned: z.boolean(),
  changeReason: z.string().nullable(),
  changeKind: z.enum(KPI_CHANGE_KINDS),
  changedBy: z.string().uuid().nullable(),
  changedByName: z.string().nullable(),
  changedByKind: z.enum(["internal", "client"]),
  changedAt: z.string(),
  /** Sürümün yazıldığı birim (eski sürümlerde yok). */
  unit: z.string().nullable().optional(),
  /** Birim değişimi olayı (changeKind=unit_change). */
  unitEvent: z
    .object({
      from: z.string().nullable(),
      to: z.string().nullable(),
      count: z.number(),
      factor: z.number().nullable(),
      mode: z.enum(["convert", "relabel"]),
    })
    .optional(),
});
export type KpiValueVersion = z.infer<typeof KpiValueVersionSchema>;

/** Restatement — onaylı değer nedenle değiştirildi (DOCX "Düzeltmeler" dipnotu). */
export const KpiRestatementSchema = z.object({
  metricId: z.string().uuid(),
  entityId: z.string().uuid(),
  year: z.number().int(),
  from: z.number().nullable(),
  to: z.number().nullable(),
  reason: z.string(),
  changedAt: z.string(),
  /** Düzeltme anındaki (dönem) birim — `from/to` bu birimdedir; yoksa göstergenin güncel birimi. */
  unit: z.string().nullable().optional(),
});
export type KpiRestatement = z.infer<typeof KpiRestatementSchema>;

// ---- Çözümlenmiş grid (resolveSetValues çıktısı — FE grid + sayaç + DOCX tek kaynak) ----
/** Kırılımlı toplam hücresinde hesap grubu dışı tam grubun doğrulama değeri (§3.2). */
export const KpiBreakdownCheckSchema = z.object({
  groupMetricId: z.string(),
  dimension: z.string(),
  rollup: z.enum(KPI_BREAKDOWN_ROLLUPS),
  status: z.enum(["filled", "calc_error", "pending_xxx"]),
  /** `avg` → üyelerin ortalaması; aksi toplam. `none` grubunda üretilmez (`isVerificationGroup`). */
  value: z.number().optional(),
  failure: z.string().optional(),
});
export type KpiBreakdownCheck = z.infer<typeof KpiBreakdownCheckSchema>;
